import type { PaginationMetaDto, PaginationResultDto } from '@bondsports/types';
import type { QueryKey } from '@tanstack/react-query';
import * as React from 'react';
import {
	useDebouncedInfiniteQuery,
	type IDebouncedInfiniteQueryPageInput,
} from './use-debounced-infinite-query';
import { useIntersectionObserverRef } from './use-intersection-observer-ref';

export interface IUseInfinitePaginationInput<TItem> {
	queryKey: QueryKey;
	/**
	 * Positional fetcher signature kept for the existing legacy callsites: page number, current
	 * debounced search string, and an optional `AbortSignal` for the underlying HTTP client.
	 */
	fetcher: (page: number, search: string, signal?: AbortSignal) => Promise<PaginationResultDto<TItem>>;
	/** Debounce window for the internal search state. Defaults to 250ms inside the shared core. */
	delay?: number;
	enabled?: boolean;
	retry?: boolean | number;
	staleTime?: number;
	cacheTime?: number;
	itemsPerPage?: number;
	onError?: (error: unknown) => void;
}

export interface IUseInfinitePaginationResult<TItem> {
	data: TItem[];
	setSearch: (next: string) => void;
	search: string;
	debouncedSearch: string;
	meta: PaginationMetaDto | null;
	/**
	 * Callback ref. Attach to the last list item (or a sentinel element) and the hook will
	 * fetch the next page when it scrolls into view.
	 */
	ref: (node: Element | null) => void;
	isLoading: boolean;
	isFetching: boolean;
	isInitialLoading: boolean;
	hasNextPage: boolean | undefined;
	fetchNextPage: () => void;
	error: Error | null;
	refetch: () => void;
}

/**
 * General-purpose infinite-pagination hook for non-`<ListPage>` consumers (Selects, dropdowns,
 * embedded scrollers). Owns its own search state and auto-attaches an `IntersectionObserver` to
 * the returned `ref` callback. For `<ListPage>` consumers, prefer `useListPageData` — both hooks
 * share the same internal core, so the React-Query/observer plumbing cannot drift.
 */
export function useInfinitePagination<TItem>({
	queryKey,
	fetcher,
	delay,
	enabled,
	retry,
	staleTime,
	cacheTime,
	itemsPerPage,
	onError,
}: IUseInfinitePaginationInput<TItem>): IUseInfinitePaginationResult<TItem> {
	const [search, setSearch] = React.useState<string>('');

	const apiMethod = React.useCallback(
		(input: IDebouncedInfiniteQueryPageInput) => fetcher(input.page, input.search ?? '', input.signal),
		[fetcher]
	);

	const result = useDebouncedInfiniteQuery<TItem>({
		queryKey,
		apiMethod,
		search,
		itemsPerPage,
		enabled,
		retry,
		staleTime,
		cacheTime,
		searchDebounceMs: delay,
		onError,
	});

	const {
		data: paginatedData,
		debouncedSearch,
		hasNextPage,
		fetchNextPage,
		isFetching,
		isLoading,
		isInitialLoading,
		error,
		refetch,
	} = result;

	const data = React.useMemo<TItem[]>(
		() => paginatedData?.pages.flatMap(page => page?.data ?? []) ?? [],
		[paginatedData]
	);

	const meta = paginatedData?.pages?.[0]?.meta ?? null;

	const handleIntersect = React.useCallback(() => {
		if (!isFetching) void fetchNextPage();
	}, [isFetching, fetchNextPage]);

	const ref = useIntersectionObserverRef({
		onIntersect: handleIntersect,
		enabled: Boolean(hasNextPage),
	});

	const wrappedFetchNextPage = React.useCallback(() => {
		void fetchNextPage();
	}, [fetchNextPage]);

	const wrappedRefetch = React.useCallback(() => {
		void refetch();
	}, [refetch]);

	return {
		data,
		setSearch,
		search,
		debouncedSearch,
		meta,
		ref,
		isLoading,
		isFetching,
		isInitialLoading: isInitialLoading ?? isLoading,
		hasNextPage,
		fetchNextPage: wrappedFetchNextPage,
		error: (error as Error | null) ?? null,
		refetch: wrappedRefetch,
	};
}
